import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, ValidationOptions } from 'react-hook-form';
import { validateRequired } from 'utils/form';
import { CloseIcon, DropIcon } from 'components/icons';
import { Loader } from 'components/loader/Loader';
import { IFile, ILogo } from 'modules/admin-panel/models';

import './dropzone.styles.scss';

export interface DropzoneProps<T> {
	acceptTypes?: string;
	acceptTypesLabels?: string[];
	wrapperClass?: string;
	id: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onUpload: (file: any) => void;
	multiple?: true;
	onDeleteUploadedData: (uploadedData: any) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	uploadedData?: T | File;
	renderUploadedData?: (uploadedData: any) => JSX.Element | null;
	required?: boolean;
	title?: string;
	validation?: ValidationOptions;
	renderExistingFiles?: boolean;
	closeIconClass?: string;
	isLoading?: boolean;
}

export interface MultipleUpload<T> extends DropzoneProps<T> {
	onUpload: (file: File[]) => void;
	multiple: true;
}

export interface SingleUpload<T> extends DropzoneProps<T> {
	onUpload: (file: File) => void;
}

export function Dropzone<T extends DropzoneProps<ILogo | IFile>>({
	acceptTypes,
	acceptTypesLabels = [],
	wrapperClass = '',
	onUpload,
	multiple,
	id,
	uploadedData,
	renderUploadedData,
	onDeleteUploadedData,
	required,
	title,
	validation,
	renderExistingFiles,
	closeIconClass,
	isLoading,
}: T) {
	const { t } = useTranslation('common');
	const { t: tr } = useTranslation('errors');

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		const fileList = e.dataTransfer && e.dataTransfer.files;
		onUploadCallback(fileList);
	};

	const onDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const fileList = e.target.files;
		onUploadCallback(fileList);
		e.target.value = '';
	};

	const onUploadCallback = (fileList: FileList | null) => {
		if (!fileList) {
			return;
		}

		if (multiple) {
			onUpload(Array.from(fileList));
		} else {
			onUpload(fileList[0]);
		}
	};

	function onClickDelete() {
		onDeleteUploadedData(uploadedData);
	}

	const { register, errors } = useFormContext();

	return (
		<>
			<input
				ref={register({
					...validateRequired(required, tr('required')),
					...validation,
				})}
				name={id}
				id={id}
				style={{
					height: 0,
					overflow: 'hidden',
					border: 'none',
					margin: 0,
					width: 0,
					padding: 0,
					position: 'absolute',
				}}
				autoComplete="off"
			/>
			{(uploadedData as IFile)?.fileKey || (uploadedData as File)?.name ? (
				<div className={`dropzone dropzone--uploaded ${wrapperClass}`}>
					<Loader isLoading={isLoading}>
						{renderUploadedData ? (
							renderUploadedData(uploadedData)
						) : (
							<p>{uploadedData}</p>
						)}
						<CloseIcon
							pointer
							iconClass={`dropzone__icon-delete ${closeIconClass}`}
							onClick={onClickDelete}
						/>
					</Loader>
				</div>
			) : (
				<div
					className={`dropzone ${wrapperClass} ${
						errors?.[id] ? 'dropzone_error' : ''
					}`}
					onDrop={handleDrop}
					onDragOver={onDragOver}
				>
					<Loader isLoading={isLoading}>
						{renderExistingFiles &&
							renderUploadedData &&
							renderUploadedData(null)}
						<DropIcon iconClass="dropzone__icon" />
						<div className="dropzone__text-wrapper">
							<span className="dropzone__text">
								{title || t('dropzone.text')}
								&nbsp;
							</span>
							<label htmlFor={`dropzone__input-${id}`}>
								<span className="dropzone__label">{t('dropzone.browse')}</span>
								<input
									onChange={handleChange}
									id={`dropzone__input-${id}`}
									name={id}
									type="file"
									accept={acceptTypes}
									multiple={multiple}
									hidden
								/>
							</label>
						</div>
						<div className="dropzone__chip-wrapper">
							{acceptTypesLabels.map((label) => (
								<span key={label} className="dropzone__chip">
									{label}
								</span>
							))}
						</div>
						{errors?.[id]?.message ||
							(errors?.[id]?.types.message && (
								<span className="group__hint group__hint--error">
									{errors?.[id]?.message || errors?.[id]?.types.message}
								</span>
							))}
					</Loader>
				</div>
			)}
		</>
	);
}
